import { ArrowRight, ArrowUpRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const categoryLabels = ["CERTIFIED", "CLINICAL", "EXPERT"];
const fallbackPosts = [{
  id: "f1",
  title: "Understanding Mold Growth in South Florida Climates",
  excerpt: "High humidity levels in Dade and Broward counties create unique challenges for indoor air quality and structural integrity.",
  link: "/ServicesPage",
  date: "2025.03.14",
  featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721825/img/mold_growth_analysis.png"
}, {
  id: "f2",
  title: "The Importance of Professional Environmental Testing",
  excerpt: "Why visual inspections are not enough to ensure your home or office is free from harmful mold spores and toxins.",
  link: "/ServicesPage",
  date: "2025.03.08",
  featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721825/img/environmental_testing_lab.webp"
}, {
  id: "f3",
  title: "26 Years of Certified Mold Inspection Expertise",
  excerpt: "Dr. MOLD of South Florida brings over two decades of clinical precision to every inspection and environmental assessment.",
  link: "/AboutPage",
  date: "2025.03.01",
  featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721825/img/certified_inspection_team.png"
}];
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}
function formatDate(date?: string): string {
  if (!date) return "";
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}.${m}.${day}`;
  } catch {
    return date;
  }
}
function ReportCard({
  title,
  excerpt,
  link,
  date,
  image,
  category,
  featured,
  index
}: {
  title: string;
  excerpt: string;
  link: string;
  date: string;
  image: string;
  category: string;
  featured: boolean;
  index: number;
}) {
  return <article data-index={index} className={`group relative flex flex-col bg-white backdrop-blur-xl border border-blue-100 shadow-lg hover:border-blue-500 transition-all duration-300 min-h-[480px] rounded-lg ${featured ? "lg:col-span-2" : "lg:col-span-1"}`}>
      {}
      <div className="relative overflow-hidden aspect-[16/10] bg-blue-50 rounded-t-lg">
        <img src={image} data-wvc-srcset={`${image}-wvc-srcset`} data-wvc-sizes={`${image}-wvc-sizes`} alt={title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
        {}
        <div className="absolute inset-0 opacity-10 mix-blend-overlay pointer-events-none" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")"
      }} />
        {}
        <div className="absolute top-0 right-0 w-16 h-16 pointer-events-none">
          <div className="absolute top-0 right-0 w-full h-[2px] bg-blue-600" />
          <div className="absolute top-0 right-0 h-full w-[2px] bg-blue-600" />
        </div>
      </div>

      {}
      <div className="flex flex-col flex-1 p-6 md:p-8 gap-4">
        <div className="flex items-center">
          <Badge variant="outline" className="border-0 border-l-2 border-l-blue-600 pl-3 pr-2 py-1 font-sans text-[11px] tracking-widest text-blue-900 bg-blue-50 rounded-md">
            {category}
          </Badge>
        </div>

        <h3 className="font-default text-2xl font-bold leading-tight text-blue-950 line-clamp-3" style={{
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden"
      }}>
          {title}
        </h3>

        <p className="font-sans text-[15px] leading-relaxed text-slate-600" style={{
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden"
      }}>
          {excerpt}
        </p>

        <div className="mt-auto pt-6 flex items-center justify-between border-t border-blue-100">
          <span className="font-sans text-xs tracking-wider text-slate-500">
            {date}
          </span>
          <Link to={link || "#"} className="inline-flex items-center text-blue-600" aria-label={`Read ${title}`}>
            <ArrowRight className="size-5 transition-transform duration-300 group-hover:translate-x-2" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </article>;
}
function IntelligenceReportsConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className={`min-h-[480px] bg-slate-100 backdrop-blur-xl border border-slate-200 shadow-md animate-pulse rounded-lg ${i === 0 ? "lg:col-span-2" : "lg:col-span-1"}`} />)}
      </div>;
  }
  const displayPosts = posts && posts.length >= 3 ? posts.slice(0, 3) : [...(posts || []), ...fallbackPosts].slice(0, 3);
  return <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {displayPosts.map((post, i) => {
      const isFallback = "featuredImage" in post && typeof (post as any).featuredImage === "string" && !(post as any).slug;
      const image = (post as any).featuredImage || (isFallback ? (post as any).featuredImage : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721825/img/default_mold_service.png");
      const excerpt = stripHtml((post as any).excerpt) || "";
      const date = formatDate((post as any).date) || (post as any).date || "";
      return <ReportCard key={post.id || i} index={i} featured={i === 0} title={post.title || ""} excerpt={excerpt} link={post.link || "#"} date={date} image={image} category={categoryLabels[i % categoryLabels.length]} />;
    })}
    </div>;
}
export default function MoldReports() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="5085" id="soroblogembed" className="bg-slate-50 text-blue-950 py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: "linear-gradient(var(--blue-900) 1px, transparent 1px), linear-gradient(90deg, var(--blue-900) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} />

      <div className="relative max-w-[1440px] mx-auto">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20">
          <div className="lg:col-span-8">
            <div className="font-sans text-xs tracking-[0.25em] text-blue-600 mb-6 font-bold">
              CLINICAL / EXPERTISE
            </div>
            <h2 className="font-default font-bold uppercase leading-[0.95] tracking-tight text-blue-950 text-4xl sm:text-5xl md:text-6xl lg:text-[64px]">
              MOLD /
              <br />
              INSIGHTS.
            </h2>
          </div>

          <div className="lg:col-span-4 flex flex-col justify-end gap-6">
            <p className="font-sans text-sm leading-relaxed text-slate-700 max-w-sm">
              Evidence-based guidance and professional insights from Dr. MOLD of South Florida. Helping homeowners and businesses maintain healthy environments across Dade, Broward, and West Palm.
            </p>
            <Link to="/ServicesPage" className="group inline-flex items-center gap-2 self-start">
              <Button variant="ghost" className="px-0 h-auto py-2 font-sans text-xs tracking-[0.25em] text-blue-950 hover:bg-transparent hover:text-blue-600 border-b border-transparent hover:border-blue-600 transition-colors rounded-none">
                VIEW ALL SERVICES
                <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
              </Button>
            </Link>
          </div>
        </div>

        {}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <IntelligenceReportsConsumer />
        </PostsProvider>
      </div>
    </section>;
}